// Desktop Normal-view layout only; deck/editor authority stays in DeckAssembler.
// `chat` (Lane 1355): an optional right-hand slot for the deck chat dock; the grid
// gives it the dock's own width, so the slide area is measured beside it.
import React, { useLayoutEffect, useRef, useState } from 'react';
const MIN = 64;
const clamp = (n, max) => Math.round(Math.min(max, Math.max(MIN, n)));
export default function DeckWorkspaceShell({ toolbar, rail, children, notes, notesOpen, onOpenNotes, chat }) {
  const anchorRef = useRef(null);
  const mainRef = useRef(null);
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [bounds, setBounds] = useState({ top: 0, left: 0, right: 0, max: MIN });
  const [chosenHeight, setChosenHeight] = useState(() => {
    try {
      const value = Number(localStorage.getItem('dk.notesH'));
      return Number.isFinite(value) && value >= MIN ? value : null;
    } catch (error) { return null; } // Optional browser preference, never deck state.
  });
  const height = clamp(chosenHeight ?? window.innerHeight * 0.24, bounds.max);
  useLayoutEffect(() => {
    const measure = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const top = Math.max(0, rect.top);
      setBounds({ top, left: rect.left, right: Math.max(0, window.innerWidth - rect.right),
        max: Math.max(MIN, Math.floor((mainRef.current.getBoundingClientRect().height
          || window.innerHeight - top) / 2)) });
    };
    window.scrollTo(0, 0);
    document.body.classList.add('dk-normal-open');
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (observer) { observer.observe(anchorRef.current); observer.observe(mainRef.current); }
    return () => {
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
      document.body.classList.remove('dk-normal-open');
    };
  }, []);
  const choose = (value) => {
    const next = clamp(value, bounds.max);
    setChosenHeight(next);
    try { localStorage.setItem('dk.notesH', String(next)); } catch (error) {
      // Storage may be denied. Resizing remains available without persistence.
    }
  };
  const endDrag = (event) => {
    if (!dragRef.current || event.pointerId !== dragRef.current.id) return;
    dragRef.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  return (
    <div className="dk-normal-anchor" ref={anchorRef}>
      <div className={`dk-normal${chat ? ' dk-normal--chat' : ''}${dragging ? ' dk-normal-dragging' : ''}`}
        data-testid="deck-normal-workspace"
        style={{ top: bounds.top, left: bounds.left, right: bounds.right, '--dk-notes-h': `${height}px` }}>
        <aside className="dk-normal-rail">{rail}</aside>
        <main className="dk-normal-main" ref={mainRef}>
          <div className="dk-normal-toolbar">{toolbar}</div>
          <div className="dk-normal-slide" data-testid="deck-slide-area">{children}</div>
          <div className="dk-normal-divider" role="separator" tabIndex={0}
            aria-label="Resize speaker notes" aria-orientation="horizontal" aria-controls="deck-normal-notes"
            aria-valuemin={notesOpen ? MIN : 44} aria-valuemax={bounds.max} aria-valuenow={notesOpen ? height : 44}
            aria-valuetext={notesOpen ? `${height} pixels` : 'Collapsed'}
            onPointerDown={(event) => {
              if (event.button !== 0) return;
              event.preventDefault(); event.currentTarget.focus();
              event.currentTarget.setPointerCapture(event.pointerId);
              dragRef.current = { id: event.pointerId, y: event.clientY, height: notesOpen ? height : 44 };
              setDragging(true);
            }}
            onPointerMove={(event) => {
              const drag = dragRef.current;
              if (drag && drag.id === event.pointerId && event.clientY !== drag.y) {
                choose(drag.height + drag.y - event.clientY);
                onOpenNotes();
              }
            }}
            onPointerUp={endDrag} onPointerCancel={endDrag} onLostPointerCapture={endDrag}
            onDoubleClick={(event) => { event.preventDefault(); choose(window.innerHeight * 0.24); onOpenNotes(); }}
            onKeyDown={(event) => {
              if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
              event.preventDefault(); choose(height + (event.key === 'ArrowUp' ? 16 : -16)); onOpenNotes();
            }} />
          <section id="deck-normal-notes" className={`dk-normal-notes${notesOpen ? '' : ' collapsed'}`}
            aria-label="Speaker notes" data-testid="deck-notes-pane">{notes}</section>
        </main>
        {chat ? <div className="dk-normal-chat">{chat}</div> : null}
      </div>
    </div>
  );
}
